import * as React from "react";
import { Nav, SearchBox, Text } from "@fluentui/react";
import { useHistory } from "react-router-dom";
import useAuth from "../auth/useAuth";

const NAV_WIDTH = 230;

const navStyles = {
    root: {
        width: NAV_WIDTH,
        minHeight: "100vh",
        height: "100%",
        boxSizing: "border-box",
        border: "1px solid #eee",
        overflowY: "auto",
        borderTop: 0,
    },
};

const containerStyle = {
    width: NAV_WIDTH,
    display: "flex",
    flexDirection: "column",
};

const searchBoxStyles = {
    // Height rather than padding so the input stretches full height and a click anywhere focuses it.
    root: {
        width: "100%",
        height: 40,
        boxSizing: "border-box",
        backgroundColor: "transparent",
        paddingLeft: 2,
    },
};
const ALL_ROLES = [
    "clientSuccessManager",
    "clientSuccessOps",
    "coach",
    "scholarship",
    "content",
    "admin",
];

const DEFAULT_REQUIRED_ROLES = ["admin"];

const navLinkGroups = [
    {
        links: [
            {
                name: "Home",
                url: "/",
                icon: "Home",
                requiredRoles: ALL_ROLES,
            },
            {
                name: "Checklist Feature",
                requiredRoles: ["clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Checklists",
                        url: "/checklist-feature/checklists",
                        icon: "CheckList",
                    },
                    {
                        name: "Action Items",
                        url: "/checklist-feature/action-items",
                        icon: "ReceiptCheck",
                    },
                    {
                        name: "Checklist Dashboards",
                        url: "/checklist-feature/checklist-dashboards",
                        icon: "ViewDashboard",
                        aliases: ["Learning Journeys"],
                    },
                ],
            },

            {
                name: "Content Feature",
                requiredRoles: ["clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Audio CMS",
                        url: "/content-feature/audio",
                        icon: "Volume3",
                        aliases: ["Audio Content Management System"],
                    },
                    // {
                    //   name: "Series",
                    //   url: "/content-feature/series",
                    //   icon: 'Settings'
                    // },
                    {
                        name: "Tiny Urls",
                        url: "/content-feature/tinyurls",
                        icon: "Settings",
                        aliases: ["Short Links"],
                    },
                    {
                      name: "Affinity Groups",
                      url: "/content-feature/affinitygroups",
                      icon: 'Group'
                    },
                    {
                        name: "Redirects",
                        url: "/content-feature/redirects",
                        icon: "Settings",
                    },
                    //{
                    //    name: "Content Summaries",
                    //    url: "/content-feature/summaries",
                    //    icon: "AlignLeft",
                    //},
                ],
            },
            {
                name: "Courses Feature",
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Courses",
                        url: "/course-feature/courses",
                        icon: "PublishCourse",
                    },
                    {
                        name: "Steps",
                        url: "/course-feature/steps",
                        icon: "PublishCourse",
                    },
                    {
                        name: "Cobrand Settings",
                        url: "/course-feature/cobrand-settings",
                        icon: "PublishCourse",
                    },
                ],
            },
            {
                name: "Credit Report Feature",
                requiredRoles: ["clientSuccessOps", "admin"],
                links: [
                    {
                        name: "Thresholding",
                        url: "/credit-report-feature/thresholding",
                        icon: "Certificate",
                        aliases: ["Credit Score", "Licensing Cap"],
                    },
                ],
            },
            {
                name: "Users Feature",
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "content", "coach", "admin"],
                links: [
                    {
                        name: "Member Lookup",
                        url: "/users-feature/member-lookup",
                        icon: "People",
                        aliases: ["User Lookup", "Find User"],
                    },
                    {
                        name: "Member Course Lookup",
                        url: "/users-feature/member-course-lookup",
                        icon: "People",
                        aliases: ["Course History", "Course Completions"],
                    },
                    {
                        name: "Sweepstakes",
                        url: "/users-feature/sweepstakes",
                        icon: "Medal",
                        aliases: ["Sprouts", "Entries"]
                    },
                    {
                        name: "Eligible users",
                        url: "/users-feature/eligible-users",
                        icon: "Medal",
                    },
                    {
                        name: "User Merge",
                        url: "/users-feature/user-merge",
                        icon: "People",
                    },
                    {
                        name: "Password Reset Tool",
                        url: "/users-feature/password-reset-tool",
                        icon: "Unlock",
                    },
                    {
                        name: "User Email Unsubscribe",
                        url: "/users-feature/member-email-unsubscribe",
                        icon: "People",
                    },
                    {
                        name: "Streak Repair",
                        url: "/users-feature/streak-repair",
                        icon: "Calendar",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        aliases: ["Streak", "Restore Streak", "Patch Streak"],
                    },
                ],
            },
            {
                name: "Other Content",
                requiredRoles: ["clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Site Notifications",
                        url: "/other-content/site-notifications",
                        icon: "Settings",
                    },
                ],
            },
            {
                name: "Client Mgmt.",
                aliases: ["Client Management"],
                requiredRoles: ["clientSuccessOps", "admin"],
                links: [
                    {
                        name: "Webhooks",
                        url: "/client-settings-feature/webhooks",
                        icon: "Certificate",
                    },
                    {
                        name: "API Keys",
                        url: "/client-settings-feature/api-keys",
                        icon: "Permissions",
                    },
                ],
            },
            {
                name: "CoBrand Mgmt.",
                aliases: ["Cobrand Management", "Cobrand Settings"],
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                links: [
                    // Setup
                    {
                        name: "Setup",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        isExpanded: true,
                        links: [
                            {
                                name: "Cobrand Launcher",
                                url: "/cobrand-settings-feature/cobrand-launcher",
                                icon: "Settings",
                            },
                            {
                                name: "Cobrand Tags",
                                url: "/cobrand-settings-feature/cobrand-tags",
                                icon: "Tag",
                            },
                            {
                                name: "Client Console Reports",
                                url: "/cobrand-settings-feature/client-console-reports",
                                icon: "Settings",
                            },
                            {
                                name: "Bulk Unit Upload",
                                url: "/cobrand-settings-feature/bulk-unit-upload",
                                icon: "Settings",
                            },
                            {
                                name: "Content API",
                                url: "/cobrand-settings-feature/content-api",
                                icon: "Settings",
                            },
                            {
                                name: "Masked Domains",
                                url: "/cobrand-settings-feature/masked-domains",
                                icon: "Settings",
                            },
                            {
                                name: "SSO Management",
                                url: "/cobrand-settings-feature/sso-management",
                                icon: "Settings",
                                requiredRoles: ["admin"],
                                aliases: ["Single Sign On", "SAML", "OIDC", "Identity Provider"],
                            },
                        ],
                    },
                    // General Settings
                    {
                        name: "General Settings",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        isExpanded: true,
                        links: [
                            {
                                name: "Hub Settings",
                                url: "/cobrand-settings-feature/hub-settings",
                                icon: "Settings",
                            },
                            {
                                name: "Disclaimer Message",
                                url: "/cobrand-settings-feature/disclaimer-message",
                                icon: "Settings",
                            },
                            {
                                name: "Terms & Policies",
                                url: "/cobrand-settings-feature/custom-policies",
                                icon: "Settings",
                                aliases: ["Privacy Policy", "Terms of Service", "Custom Policies", "Cookies", "Accessibility"],
                            },
                            {
                                name: "Benefits",
                                url: "/cobrand-settings-feature/benefits",
                                icon: "Settings",
                            },
                        ],
                    },
                    // Dashboard
                    {
                        name: "Dashboard",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        isExpanded: true,
                        links: [
                            {
                                name: "Dashboard Tasks",
                                url: "/cobrand-settings-feature/dashboard-tasks",
                                icon: "CheckList",
                            },
                            {
                                name: "Custom Tasks",
                                url: "/dashboard-feature/custom-tasks",
                                icon: "TaskManager",
                            },
                            {
                                name: "Call To Action",
                                url: "/cobrand-settings-feature/call-to-action",
                                icon: "6PointStar",
                                aliases: ["CTA"],
                            },
                            {
                                name: "Themed Monthly Tasks",
                                url: "/cobrand-settings-feature/monthly-tasks",
                                icon: "Settings",
                                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "content", "admin"],
                            },
                            {
                                name: "Alternate Themed Monthly Tasks",
                                url: "/cobrand-settings-feature/alternate-monthly-tasks",
                                icon: "Settings",
                            },
                        ],
                    },
                    // Communication
                    {
                        name: "Communication",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        isExpanded: true,
                        links: [
                            {
                                name: "Email Settings",
                                url: "/email-settings",
                                icon: "Mail",
                            },
                            {
                                name: "Email Course Completions",
                                url: "/cobrand-settings-feature/email-course-completions",
                                icon: "Mail",
                            },
                            {
                                name: "Email Senders",
                                url: "/email-senders-feature",
                                icon: "ContactList",
                            },
                            {
                                name: "Cobrand Email Senders",
                                url: "/cobrand-email-senders-feature",
                                icon: "MailForward",
                            },
                            {
                                name: "Email Template List",
                                url: "/email-template-list",
                                icon: "Mail",
                            },
                            //{
                            //    name: "Unsubscribed Users Report",
                            //    url: "/email-unsubscribes-report",
                            //    icon: "People",
                            //},
                        ],
                    },
                    // Deactivate Cobrand
                    {
                        name: "Deactivate Cobrand",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/cobrand-settings-feature/deactivate-cobrand",
                        icon: "Settings",
                    },
                    // Dashboard Advisors - NOT REORDERED (not in your list)
                    {
                        name: "Dashboard CTA",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/cobrand-settings-feature/dashboard-cta",
                        icon: "Help",
                    },
                    {
                        name: "Cobrand Units",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/client-settings-feature/cobrand-units",
                        icon: "Education",
                        aliases: ["Reporting Units", "Schools"],
                    },
                    {
                        name: "Sort Signup Units",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/client-settings-feature/cobrand-units/sort-order",
                        icon: "SortLines",
                    },
                    {
                        name: "Teams Settings",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/cobrand-settings-feature/teams",
                        icon: 'Settings'
                    },
                    {
                        name: "Featured",
                        requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                        url: "/cobrand-settings-feature/featured",
                        icon: "Settings",
                    },
                       {
                           name: "Featured Swap Tool",
                           requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                           url: "/cobrand-settings-feature/featured-swap",
                           icon: "Switch",
                       },
                ],
            },
            {
                name: "Integrated Rewards Feature",
                requiredRoles: ["clientSuccessOps", "admin"],
                links: [
                    {
                        name: "Integrated Rewards",
                        url: "/integrated-rewards-feature",
                        icon: "Settings",
                    },
                    {
                        name: "Integrated Rewards On/Off Settings",
                        url: "/integrated-rewards-feature/settings",
                        icon: "Settings",
                    },
                ],
            },
            {
                name: "Mindfulness Feature",
                requiredRoles: ["clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Journals",
                        url: "/mindfulness-feature/journals",
                        icon: "Settings",
                    },
                    {
                        name: "Mindfulness Program",
                        url: "/mindfulness-feature/mindfulness-program",
                        icon: "Settings",
                    },
                ],
            },
            {
                name: "Scholarships Feature",
                requiredRoles: ["clientSuccessOps", "content", "scholarship", "admin"],
                links: [
                    {
                        name: "Scholarship Inventory",
                        url: "/scholarships-feature/scholarships",
                        icon: "Settings",
                    },
                    {
                        name: "Field of Study Categories",
                        url: "/scholarships-feature/field-of-study",
                        icon: "Settings",
                    },
                ],
            },
            /*
      {
        name: "Google Analytics Feature",
        links: [
          {
            name: "GA Settings",
            url: "/google-analytics-feature/settings",
            icon: 'Settings' // You might want to choose a more specific icon
          }
        ]
      },
      */
            {
                name: "Icon Viewer Feature",
                requiredRoles: ["content", "admin"],
                links: [
                    {
                        name: "Icon Viewer",
                        url: "/icon-viewer-feature",
                        icon: "Settings",
                    },
                ],
            },
            {
                name: "Audits",
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Audits",
                        url: "/audits",
                        icon: "Settings",
                        aliases: ["reports"],
                    },
                ],
            },
            {
                name: "Reporting",
                aliases: ["reports"],
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "content", "admin"],
                links: [
                    {
                        name: "Report Users",
                        url: "/reporting/report-users",
                        icon: "BarChartVertical",
                    },
                ],
            },
            {
                name: "Question DB",
                aliases: ["Question Database"],
                requiredRoles: ["content", "admin"],
                links: [
                    {
                        name: "Attribute Cobrand Settings",
                        url: "/question-db/attribute-cobrand-settings",
                        icon: "Database",
                    },
                ],
            },
            {
                name: "Campaigns Feature",
                requiredRoles: ["clientSuccessManager", "clientSuccessOps", "admin"],
                links: [
                    {
                        name: "Campaigns",
                        url: "/campaigns-feature/campaigns",
                        icon: "Megaphone",
                    },
                ],
            },
            {
              name: "Webinars Feature",
              requiredRoles: ["content", "admin"],
              links: [
                {
                  name: "Webinars",
                  url: "/webinars-feature",
                  icon: 'Settings'
                },
              ],
            },
        ],
    },
];

const linkMatchesTerm = (link, term) => {
    const candidates = [link.name, ...(link.aliases || [])];
    return candidates.some((candidate) => (candidate || "").toLowerCase().includes(term));
};

// Fluent's Nav reads `isExpanded` off the link objects, so expanding is done by rebuilding them.
const expandAll = (links) =>
    links.map(({ aliases, ...link }) =>
        link.links ? { ...link, links: expandAll(link.links), isExpanded: true } : link
    );

const stripAliases = (links) =>
    links.map(({ aliases, ...link }) => (link.links ? { ...link, links: stripAliases(link.links) } : link));

const filterLinksBySearch = (links, term) =>
    links.reduce((acc, link) => {
        const { aliases, ...rest } = link;

        if (linkMatchesTerm(link, term)) {
            acc.push(rest.links ? { ...rest, links: expandAll(rest.links), isExpanded: true } : rest);
            return acc;
        }

        const matchingChildren = rest.links ? filterLinksBySearch(rest.links, term) : undefined;
        if (matchingChildren && matchingChildren.length) {
            acc.push({ ...rest, links: matchingChildren, isExpanded: true });
        }

        return acc;
    }, []);

export const NavMenu = () => {
    let history = useHistory();
    const { hasAnyRole } = useAuth();
    const [searchText, setSearchText] = React.useState("");

    const getIsAllowed = React.useCallback(
        (roles) => {
            if (!roles || roles.length === 0) {
                return true;
            }
            return hasAnyRole(roles);
        },
        [hasAnyRole]
    );

    const filterLinks = React.useMemo(() => {
        const filter = (links, inheritedRoles) => {
            return links.reduce((acc, link) => {
                const roles = link.requiredRoles || inheritedRoles || DEFAULT_REQUIRED_ROLES;
                const childLinks = link.links ? filter(link.links, roles) : undefined;

                const isAllowed = getIsAllowed(roles);
                const hasVisibleChildren = Array.isArray(childLinks) && childLinks.length > 0;

                if (isAllowed || hasVisibleChildren) {
                    const { requiredRoles, ...rest } = link;
                    acc.push({
                        ...rest,
                        links: childLinks,
                    });
                }

                return acc;
            }, []);
        };

        return filter;
    }, [getIsAllowed]);

    const filteredGroups = React.useMemo(() => {
        return navLinkGroups
            .map((group) => {
                const filteredLinks = filterLinks(group.links, DEFAULT_REQUIRED_ROLES);
                return filteredLinks.length
                    ? {
                          ...group,
                          links: filteredLinks,
                      }
                    : null;
            })
            .filter(Boolean);
    }, [filterLinks]);

    const searchedGroups = React.useMemo(() => {
        const term = searchText.trim().toLowerCase();

        return filteredGroups
            .map((group) => {
                const links = term ? filterLinksBySearch(group.links, term) : stripAliases(group.links);
                return links.length ? { ...group, links } : null;
            })
            .filter(Boolean);
    }, [filteredGroups, searchText]);

    function onLinkClick(ev, item) {
        ev.preventDefault();
        if (item) {
            history.push(item.url);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }

    if (!filteredGroups.length) {
        return null;
    }

    return (
        <div style={containerStyle}>
            <SearchBox
                className="nav-search-box"
                placeholder="Search"
                ariaLabel="Search navigation menu"
                showIcon
                value={searchText}
                onChange={(_, newValue) => setSearchText(newValue || "")}
                onClear={() => setSearchText("")}
                styles={searchBoxStyles}
            />
            {searchedGroups.length ? (
                <Nav onLinkClick={onLinkClick} styles={navStyles} groups={searchedGroups} />
            ) : (
                <Text styles={{ root: { padding: 12, color: "#605e5c" } }}>No matching menu items</Text>
            )}
        </div>
    );
};
